import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { cloneDeep } from 'es-toolkit';

import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FieldSet,
  Form,
  FormField,
  FormHelp,
  Icon,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Text,
  TextButton,
  ToggleButton,
} from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyCriteriaWebApiHandler_set } from '@zg-rentals/rental-manager-env';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useIsOnReviewStep } from '../../../../hooks/index';
import {
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
} from '../../../../hooks/queries/multiplex';
import useMultiplexSetListingCriteria, {
  mapCriterionToMultiplexListingCriterionRequest,
} from '../../../../hooks/queries/multiplex/useMultiplexSetListingCriteria';
import { AppSelectors, PropertiesSelectors, UserSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import { listingCriteriaConstraints } from '../../../../utils/formValidation';
import wizardActions from '../../../Wizard/actions';

import { TRIGGER_LOCATIONS, TRIGGER_OBJECT } from '../../../../constants/ClickstreamConstants';
import { TYPES_MAP } from '../../../../constants/ListingCriteriaConstants';
import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import PetPolicyAndPetFeeCSEvents from '../../../../model/ClickstreamEvent/PetPolicyAndPetFeeCSEvents';
import ListingCriteria from '../../../../model/ListingCriteria/index';
import PetsAllowed from '../../../../model/PetsAllowed';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { asteriskAfterStyles } from '../../../../styles/text';

import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';
import PetRow from './PetRow';

import type { ChangeEvent } from 'react';
import type { UserPropertyCriteriaSetRequestWebDto } from '@zg-rentals/rental-manager-env';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';
import type { CriterionProps } from '../../../../model/ListingCriteria/Criterion';

type MockEventProps = {
  target: {
    name: string;
    value: string | boolean | Record<string, CriterionProps>;
    checked?: string | boolean;
  };
  persist?: () => void;
};

type SaveEvent = ChangeEvent<HTMLInputElement> | MockEventProps;

const criteriaList = [
  TYPES_MAP.PET_POLICY_CAT_COUNT,
  TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT,
  TYPES_MAP.PET_POLICY_PET_DEPOSIT,
  TYPES_MAP.PET_POLICY_PET_RENT,
  TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT,
];

// input fields that require checks against for pets allowed
const criteriaPetCountToValidateList = [
  TYPES_MAP.PET_POLICY_CAT_COUNT,
  TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT,
  TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT,
];

// input fields that need client side validation
const criteriaToValidateList = [TYPES_MAP.PET_POLICY_PET_DEPOSIT, TYPES_MAP.PET_POLICY_PET_RENT];

const MIN_PET_COUNT = 0;
const MAX_PET_COUNT = 9;

const EMPTY_CRITERIA: Array<CriterionProps> = [];

const inputArePetsAllowed = 'inputArePetsAllowed';
const inputIsPetPolicyNegotiable = 'inputIsPetPolicyNegotiable';
const inputIsPetPolicyRefundable = 'inputIsPetPolicyRefundable';
const inputCriteriaMap = 'inputCriteriaMap';

const PET_COUNT_ERROR = {
  KEY: 'petCounts',
  MESSAGE: 'Add at least one pet type to continue',
};

const getInitialCriteriaMap = (currentCriteria: Array<CriterionProps>) => {
  const criteriaSet = new Set(criteriaList);

  const targetCriteriaList: Array<CriterionProps> = currentCriteria.filter(
    (obj: CriterionProps) => criteriaSet.has(obj.type as (typeof criteriaList)[number]) && obj.type !== '',
  );

  const criteriaMap = criteriaList.reduce((acc: Record<string, Partial<CriterionProps>>, criteriaType) => {
    const criterion = targetCriteriaList.find((obj: CriterionProps) => obj.type === criteriaType) || {};

    acc[criteriaType] = {
      ...criterion,
    };

    return acc;
  }, {});

  return criteriaMap;
};

/**
 * Returns true if any of the criteria in criteriaMap have a non-zero value and at least one criterion has a non-empty value.
 * Returns null if no criteria have a non-empty value.
 *
 * @param {Record<string, CriterionProps>} criteriaMap
 * @returns {boolean | null}
 */
const getIsPetsAllowed = (criteriaMap: Record<string, Partial<CriterionProps>>) => {
  const criteriaToCheck = criteriaPetCountToValidateList.map((key) => criteriaMap[key]);

  const hasNonEmptyCriterion = criteriaToCheck.some((criterion) => {
    return criterion?.criterion !== '';
  });

  // indicates no selections have been made
  if (!hasNonEmptyCriterion) return null;

  /**
   * a selection has been made
   * updates empty string values to be 0
   */
  const hasNonZeroCriterion = criteriaToCheck.some((criterion) => {
    if (criterion?.criterion === '') {
      criterion.criterion = '0';
    }
    return criterion?.criterion !== '0';
  });

  return hasNonZeroCriterion;
};

const getIsPetsAllowedViaDTO = (petsAllowed: {
  smallDogsDto: { allowed: boolean; count: number };
  largeDogsDto: { allowed: boolean; count: number };
  catsDto: { allowed: boolean; count: number };
}) => {
  // loop through petsAllowed and check
  const arePetsAllowedViaDTO =
    petsAllowed.smallDogsDto.allowed || petsAllowed.largeDogsDto.allowed || petsAllowed.catsDto.allowed;

  // return false or fallback to null
  return arePetsAllowedViaDTO;
};

const getPetsAllowed = (inputCriteriaMap: Record<string, Partial<CriterionProps>>) => {
  const catCount = Number(inputCriteriaMap[TYPES_MAP.PET_POLICY_CAT_COUNT].criterion);
  const smallDogCount = Number(inputCriteriaMap[TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT].criterion);
  const largeDogCount = Number(inputCriteriaMap[TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT].criterion);

  return PetsAllowed({
    cats: catCount !== 0,
    catsDto: { allowed: catCount !== 0, count: catCount || null },
    smallDogs: smallDogCount !== 0,
    smallDogsDto: {
      allowed: smallDogCount !== 0,
      count: smallDogCount || null,
    },
    largeDogs: largeDogCount !== 0,
    largeDogsDto: {
      allowed: largeDogCount !== 0,
      count: largeDogCount || null,
    },
  });
};

const formatDataToSave = (inputCriteriaMap: Record<string, Partial<CriterionProps>>) => {
  return Object.entries(inputCriteriaMap).map(([key, value]) => {
    const { criterion, negotiable, isEnabled, refundable } = value;

    let finalizedCriterion = criterion;
    if (criteriaPetCountToValidateList.includes(key as (typeof criteriaPetCountToValidateList)[number])) {
      finalizedCriterion = finalizedCriterion || '0';
    }

    return { criterion: finalizedCriterion, negotiable, isEnabled, type: key, refundable };
  });
};

const PetsAllowedFormField = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { canAccessSFCostsAndFees, isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const isOnReviewStep = useIsOnReviewStep();

  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const { data: multiplexDetailsData } = useMultiplexDetails({ enabled: !isOnReviewStep });

  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const user = useSelector((state) => UserSelectors.makeUser(state));

  const [displayChangeYourPetPolicyModal, setDisplayChangeYourPetPolicyModal] = useState<boolean>(false);

  const { mutateAsync: updateListingCriteria } = useRentalManagerUserPropertyCriteriaWebApiHandler_set();

  const { mutateAsync: updateMultiplexListingCriteria } = useMultiplexSetListingCriteria();

  const { listingAlias, petsAllowed } = currentProperty;

  const currentCriteria = currentProperty.listingCriteria?.criteria ?? EMPTY_CRITERIA;

  const criteriaForForm: Array<CriterionProps> =
    isOnReviewStep === false && isInMultiplexListingFlow && multiplexDetailsData?.listingCriteria?.criteria
      ? (multiplexDetailsData.listingCriteria.criteria as Array<CriterionProps>)
      : currentCriteria;

  const initialCriteriaMap = getInitialCriteriaMap(cloneDeep(criteriaForForm));

  // can use any pet criteria key
  // const isPetPolicyNegotiable = initialCriteriaMap?.[TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT]?.negotiable;
  const isPetPolicyNegotiable =
    initialCriteriaMap?.[TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT]?.negotiable ||
    initialCriteriaMap?.[TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT]?.negotiable ||
    initialCriteriaMap?.[TYPES_MAP.PET_POLICY_CAT_COUNT]?.negotiable;

  const arePetsAllowed = getIsPetsAllowed(initialCriteriaMap);
  const arePetsAllowedViaDTO = getIsPetsAllowedViaDTO(petsAllowed);
  const isPetPolicyRefundable = initialCriteriaMap?.[TYPES_MAP.PET_POLICY_PET_DEPOSIT]?.refundable;

  const {
    addErrorKey,
    clearFieldError,
    inputs,
    errorObj,
    hasErrors,
    hasFieldError,
    handleChange: handleFormChange,
    updateForm,
  } = useForm(
    {
      [inputIsPetPolicyNegotiable]: isPetPolicyNegotiable,
      // cross reference with arePetsAllowedDTO?
      [inputArePetsAllowed]: arePetsAllowedViaDTO === true || arePetsAllowed === true,
      [inputCriteriaMap]: initialCriteriaMap,
      [inputIsPetPolicyRefundable]: isPetPolicyRefundable,
    },
    listingCriteriaConstraints,
  );

  useEffect(() => {
    if (!isInMultiplexListingFlow || !multiplexDetailsData?.listingCriteria?.criteria) {
      return;
    }

    const map = getInitialCriteriaMap(
      cloneDeep(multiplexDetailsData.listingCriteria.criteria as Array<CriterionProps>),
    );
    const nextIsPetPolicyNegotiable =
      map?.[TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT]?.negotiable ||
      map?.[TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT]?.negotiable ||
      map?.[TYPES_MAP.PET_POLICY_CAT_COUNT]?.negotiable;
    const nextArePetsAllowed = getIsPetsAllowed(map);
    const nextArePetsAllowedViaDTO = getIsPetsAllowedViaDTO(petsAllowed);
    const nextIsPetPolicyRefundable = map?.[TYPES_MAP.PET_POLICY_PET_DEPOSIT]?.refundable;

    updateForm({
      [inputIsPetPolicyNegotiable]: nextIsPetPolicyNegotiable,
      [inputArePetsAllowed]: nextArePetsAllowedViaDTO === true || nextArePetsAllowed === true,
      [inputCriteriaMap]: map,
      [inputIsPetPolicyRefundable]: nextIsPetPolicyRefundable,
    });
  }, [isInMultiplexListingFlow, multiplexDetailsData?.listingCriteria?.criteria]);

  const prevValue = useRef('');
  const isInitialCriteriaMapSyncRef = useRef(true);
  const prevValueArePetsAllowed = useRef(inputs.inputArePetsAllowed);
  const isPetsAllowedCheckboxDisabled = inputs?.inputArePetsAllowed === null;

  const { active } = currentProperty;
  const triggerLocation = active ? TRIGGER_LOCATIONS.POST_ACTIVATION : TRIGGER_LOCATIONS.POSTING_PATH;
  const triggerObject = isInModalState ? TRIGGER_OBJECT.REVIEW : TRIGGER_OBJECT.PET_POLICY_PAGE;

  useEffect(() => {
    if (isSaveComplete) {
      const mockEvent: SaveEvent = {
        target: {
          name: inputCriteriaMap,
          value: inputs.inputArePetsAllowed === false ? resetAllPetCounts() : inputs.inputCriteriaMap,
        },
      };

      saveDebounced(mockEvent);
    }
  }, [isSaveComplete]);

  useEffect(() => {
    const hasPetsAllowedChanged = prevValueArePetsAllowed.current !== inputs.inputArePetsAllowed;

    if (hasPetsAllowedChanged) {
      // @ts-expect-error
      handlePetsAllowedChange(prevValueArePetsAllowed.current, inputs.inputArePetsAllowed);
    }

    prevValueArePetsAllowed.current = inputs.inputArePetsAllowed;
  }, [inputs.inputArePetsAllowed]);

  useEffect(() => {
    validatePetCount();

    if (isInitialCriteriaMapSyncRef.current) {
      isInitialCriteriaMapSyncRef.current = false;
      prevValue.current = JSON.stringify(inputs.inputCriteriaMap);
      return;
    }

    const mockEvent: SaveEvent = {
      target: {
        name: inputCriteriaMap,
        // @ts-expect-error
        value: inputs.inputCriteriaMap,
      },
    };

    /**
     * we need to do auto save on initial load to ensure the criteria are saved
     * to avoid confusion on inferring are pets allowed selection from default values
     */
    saveDebounced(mockEvent);
  }, [inputs.inputCriteriaMap]);

  useEffect(() => {
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: hasErrors });
    // keep save disabled in sync with field errors (normally we do not do this)
    actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: hasErrors });
  }, [hasErrors]);

  const resetAllPetCounts = () => {
    // @ts-expect-error
    const updatedInputCriteriaMap = { ...inputs.inputCriteriaMap };

    criteriaPetCountToValidateList.forEach((key) => {
      updatedInputCriteriaMap[key] = { ...updatedInputCriteriaMap[key], criterion: '0' };
    });

    criteriaToValidateList.forEach((key) => {
      updatedInputCriteriaMap[key] = { ...updatedInputCriteriaMap[key], criterion: '' };
    });

    return updatedInputCriteriaMap;
  };

  const areAllPetCountsZero = () => {
    // @ts-expect-error
    return criteriaPetCountToValidateList.every((key) => inputs.inputCriteriaMap[key].criterion === '0');
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveMultiplexListingCriteria = async ({
    dataArray,
    fieldName,
    value,
  }: {
    dataArray: Array<UserPropertyCriteriaSetRequestWebDto>;
    fieldName: string;
    value: unknown;
  }) => {
    /**
     * Defensive guard only: multiplex save runs when `isInMultiplexListingFlow`, which should already imply a
     * session id on the URL. `useMultiplexSetListingCriteria` also rejects empty ids before any request.
     */
    if (!multiplexBuildingSessionId) {
      logError({
        error: new Error('Unable to save: missing multiplexBuildingSessionId'),
        context: {
          details: 'PetsAllowedFormField.saveMultiplexListingCriteria',
          fieldName,
        },
      });

      return;
    }

    try {
      const response = await updateMultiplexListingCriteria({
        getParams: { multiplexBuildingSessionId },
        postData: {
          listingCriteria: dataArray.map((row) =>
            mapCriterionToMultiplexListingCriterionRequest(row as CriterionProps),
          ),
        },
      });

      if (response?.success === false) {
        updateServerError(response.errorMessage ?? 'Unable to save listing criteria.', fieldName);
        return;
      }

      prevValue.current = JSON.stringify(value);
    } catch (err: unknown) {
      const message =
        typeof err === 'object' &&
        err !== null &&
        'error' in err &&
        typeof (err as { error?: { message?: string } }).error === 'object'
          ? (err as { error: ErrorRMWProps }).error.message
          : DatarouterError.is(err)
            ? DatarouterError.getDisplayMessage(err)
            : ((err as { message?: string })?.message ?? '');

      updateServerError(message, fieldName);
    }
  };

  const save = (event: SaveEvent) => {
    const { value = '', name: fieldName } = event.target;

    if (hasErrors) {
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const isValueChanging = JSON.stringify(value) !== JSON.stringify(prevValue.current);

    // always updateListingCriteria if are pets allowed is false ('no')
    if (inputs.inputArePetsAllowed === true && isValueChanging === false) {
      // short circuit updateListingCriteria if are pets allowed is true and no changes have been made
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    let formattedDataToSave = null;
    if (fieldName === inputCriteriaMap) {
      if (typeof value === 'object' && value !== null) {
        formattedDataToSave = {
          criteria: formatDataToSave(value as Record<string, Partial<CriterionProps>>),
        };
      } else {
        console.error('Invalid value type for inputCriteriaMap');
      }
    }

    if (formattedDataToSave === null) {
      return;
    }

    const dataArray: Array<UserPropertyCriteriaSetRequestWebDto> =
      formattedDataToSave?.criteria as Array<UserPropertyCriteriaSetRequestWebDto>;

    if (isOnReviewStep === false && isInMultiplexListingFlow) {
      return saveMultiplexListingCriteria({ dataArray, fieldName, value });
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    updateListingCriteria({
      getParams: {
        listingAlias,
      },
      postData: dataArray,
    })
      .then(() => {
        dispatch(
          PropertiesActions.updateCurrentProperty({
            listingCriteria: ListingCriteria({
              isEnabled: true,
              criteria: [...currentCriteria, ...dataArray],
            }),
            petsAllowed: getPetsAllowed(inputs.inputCriteriaMap as Record<string, Partial<CriterionProps>>),
          }),
        );

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: DatarouterError) => {
        updateServerError(err?.message || 'Something went wrong', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  // Ref avoids stale `save` inside debounce timers.
  const saveRef = useRef(save);
  saveRef.current = save;

  const saveDebounced = useMemo(
    () => debounce((event: SaveEvent) => saveRef.current(event), TimeConstants.SELECTION_DEBOUNCE_TIME_MS),
    [],
  );

  /**
   *
   * updates a criteria map with new data. It can update either a single key in the map or the entire map
   */
  const updateCriteriaMap = (
    newCriterion: string | Record<string, CriterionProps>,
    inputCriteriaKey?: string,
    bypassSave?: boolean,
  ) => {
    let updatedCriteriaMap: Record<string, CriterionProps>;

    if (inputCriteriaKey && typeof newCriterion === 'string') {
      // updating single key
      updatedCriteriaMap = {
        // @ts-expect-error
        ...inputs?.inputCriteriaMap,
        [inputCriteriaKey]: {
          // @ts-expect-error
          ...inputs?.inputCriteriaMap[inputCriteriaKey],
          criterion: newCriterion,
        },
      };
    } else if (typeof newCriterion === 'object' && newCriterion !== null) {
      // updating entire map
      updatedCriteriaMap = {
        ...newCriterion,
      } as Record<string, CriterionProps>;
    } else {
      throw new Error('Invalid arguments passed to updateCriteriaMap');
    }

    const mockEvent: SaveEvent = {
      target: {
        name: inputCriteriaMap,
        value: updatedCriteriaMap,
      },
    };

    clearServerError();
    // @ts-expect-error
    handleFormChange(mockEvent);

    if (bypassSave) {
      return;
    }
  };

  const validatePetCount = () => {
    let response = null;

    if (inputs.inputArePetsAllowed === true && areAllPetCountsZero()) {
      response = false;
      addErrorKey(PET_COUNT_ERROR.KEY, PET_COUNT_ERROR.MESSAGE);
    } else {
      response = true;
      clearFieldError(PET_COUNT_ERROR.KEY);
    }

    return response;
  };

  const handlePetsAllowedChange = (prevValue: boolean | null, currentValue: boolean | null) => {
    if (prevValue !== currentValue) {
      if (currentValue === false && prevValue === true) {
        if (isInModalState) {
          updateCriteriaMap(resetAllPetCounts(), '', true);
        } else {
          setDisplayChangeYourPetPolicyModal(true);
        }
      } else if (prevValue === null) {
        const resetMap = resetAllPetCounts();
        updateCriteriaMap(resetMap);
        if (isOnReviewStep === false && isInMultiplexListingFlow) {
          saveDebounced({
            target: {
              name: inputCriteriaMap,
              value: resetMap,
            },
          });
        }
      }
    }

    validatePetCount();

    if (currentValue === true) {
      PetPolicyAndPetFeeCSEvents.PetsAllowedYes({ currentProperty, user, triggerObject });
    } else {
      PetPolicyAndPetFeeCSEvents.PetsAllowedNo({ currentProperty, user, triggerObject });
    }
  };

  const handleNegotiableChange = (event: SaveEvent) => {
    const { checked, name } = event.target;

    if (typeof checked === 'boolean') {
      clearServerError();

      // bypass handleFormChange to ensure synchronous update of input data, preventing brief desync with updateCriteriaMap
      inputs[name] = checked;

      // update all pet fields' 'negotiable' key to the value of 'checked'
      // @ts-expect-error
      const newCriterion = Object.keys(inputs.inputCriteriaMap).reduce((acc: Record<string, CriterionProps>, key) => {
        // @ts-expect-error
        acc[key] = { ...inputs.inputCriteriaMap[key], negotiable: checked };
        return acc;
      }, {});

      updateCriteriaMap(newCriterion);
      if (isOnReviewStep === false && isInMultiplexListingFlow) {
        saveDebounced({
          target: {
            name: inputCriteriaMap,
            value: newCriterion,
          },
        });
      }
      PetPolicyAndPetFeeCSEvents.PetPolicyNegotiableCheckbox({
        currentProperty,
        triggerLocation,
        triggerObject,
        user,
      });
    }
  };

  const handleRefundableChange = (event: SaveEvent) => {
    const { checked, name } = event.target;

    if (typeof checked === 'boolean') {
      clearServerError();

      // bypass handleFormChange to ensure synchronous update of input data, preventing brief desync with updateCriteriaMap
      inputs[name] = checked === false;

      const updatedPetDepositCriterion = {
        // @ts-expect-error
        ...inputs.inputCriteriaMap[TYPES_MAP.PET_POLICY_PET_DEPOSIT],
        refundable: checked === false,
      };

      const newCriterion = {
        // @ts-expect-error
        ...inputs.inputCriteriaMap,
        [TYPES_MAP.PET_POLICY_PET_DEPOSIT]: updatedPetDepositCriterion,
      };

      updateCriteriaMap(newCriterion);
      if (isOnReviewStep === false && isInMultiplexListingFlow) {
        saveDebounced({
          target: {
            name: inputCriteriaMap,
            value: newCriterion,
          },
        });
      }
    }
  };

  const handleArePetsAllowedChange = (arePetsAllowed: boolean) => {
    const mockEvent: SaveEvent = {
      target: {
        name: inputArePetsAllowed,
        value: arePetsAllowed,
      },
    };

    clearServerError();
    // @ts-expect-error
    handleFormChange(mockEvent);
  };

  const handleInputFieldChange = (event: SaveEvent) => {
    const { value: eventValue, name: inputCriteriaKey } = event.target;

    updateCriteriaMap(eventValue as string, inputCriteriaKey, true);

    // handles the input field that is changing
    if (!hasFieldError(inputCriteriaKey, eventValue)) {
      clearFieldError(inputCriteriaKey);
    }
  };

  const handleInputFieldBlur = (event: SaveEvent) => {
    const { value: eventValue, name: inputCriteriaKey } = event.target;

    if (isOnReviewStep === false && isInMultiplexListingFlow) {
      const updatedCriteriaMap = {
        // @ts-expect-error
        ...inputs?.inputCriteriaMap,
        [inputCriteriaKey]: {
          // @ts-expect-error
          ...inputs?.inputCriteriaMap[inputCriteriaKey],
          criterion: String(eventValue),
        },
      };

      updateCriteriaMap(eventValue as string, inputCriteriaKey, true);
      saveDebounced({
        target: {
          name: inputCriteriaMap,
          value: updatedCriteriaMap,
        },
      });
      return;
    }

    updateCriteriaMap(eventValue as string, inputCriteriaKey);
  };

  const handleCountChange = (event: React.MouseEvent<HTMLButtonElement>, delta: number) => {
    const inputCriteriaKey = event.currentTarget.name;
    // @ts-expect-error
    const currentCriterion = inputs?.inputCriteriaMap[inputCriteriaKey].criterion || '0';

    const value = Math.min(MAX_PET_COUNT, Math.max(MIN_PET_COUNT, parseInt(currentCriterion, 10) + delta)).toString();

    if (isOnReviewStep === false && isInMultiplexListingFlow) {
      const updatedCriteriaMap = {
        // @ts-expect-error
        ...inputs?.inputCriteriaMap,
        [inputCriteriaKey]: {
          // @ts-expect-error
          ...inputs?.inputCriteriaMap[inputCriteriaKey],
          criterion: value,
        },
      };

      updateCriteriaMap(value, inputCriteriaKey, true);
      saveDebounced({
        target: {
          name: inputCriteriaMap,
          value: updatedCriteriaMap,
        },
      });
    } else {
      updateCriteriaMap(value, inputCriteriaKey);
    }

    switch (inputCriteriaKey) {
      case TYPES_MAP.PET_POLICY_CAT_COUNT:
        PetPolicyAndPetFeeCSEvents.NumberOfCatsAllowed({ currentProperty, user, triggerObject, triggerLocation });
        break;
      case TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT:
        PetPolicyAndPetFeeCSEvents.NumberOfSmallDogsAllowed({ currentProperty, user, triggerObject });
        break;
      case TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT:
        PetPolicyAndPetFeeCSEvents.NumberOfLargeDogsAllowed({ currentProperty, user, triggerObject });
        break;
      default:
        console.info('No case for: ' + inputCriteriaKey);
        break;
    }
  };

  const renderPetsAllowed = () => {
    return (
      <div data-testid="PetsAllowedFormField">
        <Box
          data-testid="PetsAllowedHeader"
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '400',
          }}
        >
          <Text textStyle="body-sm-bold" className="fs-unmask">
            Pet type
          </Text>
          <Text textStyle="body-sm-bold" css={{ marginRight: '600' }} className="fs-unmask">
            Maximum
          </Text>
        </Box>
        <div data-testid="PetsAllowedRowContainer">
          <PetRow
            label="Cats"
            name={TYPES_MAP.PET_POLICY_CAT_COUNT}
            // @ts-expect-error
            value={inputs.inputCriteriaMap[TYPES_MAP.PET_POLICY_CAT_COUNT].criterion}
            onPlusClick={(event) => handleCountChange(event, 1)}
            onMinusClick={(event) => handleCountChange(event, -1)}
          />
          <PetRow
            label="Small dogs (under 40 lb)"
            name={TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT}
            // @ts-expect-error
            value={inputs.inputCriteriaMap[TYPES_MAP.PET_POLICY_SMALL_DOG_COUNT].criterion}
            onPlusClick={(event) => handleCountChange(event, 1)}
            onMinusClick={(event) => handleCountChange(event, -1)}
          />
          <PetRow
            label="Large dogs (over 40 lb)"
            name={TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT}
            // @ts-expect-error
            value={inputs.inputCriteriaMap[TYPES_MAP.PET_POLICY_LARGE_DOG_COUNT].criterion}
            onPlusClick={(event) => handleCountChange(event, 1)}
            onMinusClick={(event) => handleCountChange(event, -1)}
          />
        </div>
      </div>
    );
  };

  const renderPetsNotAllowed = () => {
    return <Alert>You aren&apos;t required to allow pets, but may still be asked to allow assistance animals.</Alert>;
  };

  const handleModalContinueClick = () => {
    setDisplayChangeYourPetPolicyModal(false);
    const resetMap = resetAllPetCounts();
    updateCriteriaMap(resetMap);
    if (isOnReviewStep === false && isInMultiplexListingFlow) {
      saveDebounced({
        target: {
          name: inputCriteriaMap,
          value: resetMap,
        },
      });
    }
  };
  const handleCancelClick = () => {
    handleCloseClick();
  };

  const handleCloseClick = () => {
    setDisplayChangeYourPetPolicyModal(false);

    const name = inputArePetsAllowed;

    const mockEvent: SaveEvent = {
      target: {
        name,
        value: true,
      },
    };

    // @ts-expect-error
    handleFormChange(mockEvent);
  };

  return (
    <Form asChild={true} data-testid="PetsAllowedFormField">
      <div>
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

        {isInModalState === true && (
          <Paragraph css={{ marginBottom: '400' }}>
            59% of renters have at least one pet, according to the 2023 Zillow Consumer Housing Trends Report. Allow
            pets to reach a larger group of prospective renters.
          </Paragraph>
        )}

        <FieldSet data-testid="PetsAllowedFieldSet">
          {/* using Text as Label does not support size more than lg */}
          <Text textStyle="heading-xs" css={{ marginBottom: '600', ...asteriskAfterStyles }} asChild>
            <p>Are pets allowed?</p>
          </Text>

          <FormField data-testid="PetsAllowedToggleButtonContainer" aria-labelledby="pets-allowed-label">
            <ToggleButton
              name="PetsAllowed"
              selected={inputs?.inputArePetsAllowed === true}
              onSelectedChange={(selected) => selected && handleArePetsAllowedChange(true)}
              css={{ marginRight: '200' }}
              data-eventid={PetPolicyAndPetFeeCSEvents.PetsAllowedYes.data_event_id}
            >
              Yes
            </ToggleButton>

            <ToggleButton
              name="PetsAllowed"
              selected={inputs?.inputArePetsAllowed === false}
              onSelectedChange={(selected) => selected && handleArePetsAllowedChange(false)}
              data-eventid={PetPolicyAndPetFeeCSEvents.PetsAllowedNo.data_event_id}
            >
              No
            </ToggleButton>
          </FormField>

          <FormField data-testid="PetsAllowedConditionalFormField">
            {inputs?.inputArePetsAllowed !== null && (
              <div data-testid="PetsAllowedConditionalForm">
                {inputs?.inputArePetsAllowed === true ? renderPetsAllowed() : renderPetsNotAllowed()}
              </div>
            )}
          </FormField>

          {/* @ts-expect-error */}
          {!!errorObj[PET_COUNT_ERROR.KEY] && (
            <Box css={{ marginBottom: '600' }}>
              <FormHelp error={true}>
                {/* @ts-expect-error */}
                {errorObj[PET_COUNT_ERROR.KEY]}
              </FormHelp>
            </Box>
          )}

          <FormField>
            <LabeledControl
              label={<Label>Pet policy is negotiable</Label>}
              disabled={isPetsAllowedCheckboxDisabled}
              control={
                <Checkbox
                  name={inputIsPetPolicyNegotiable}
                  checked={inputs[inputIsPetPolicyNegotiable] as boolean}
                  onChange={handleNegotiableChange}
                  data-eventid={PetPolicyAndPetFeeCSEvents.PetPolicyNegotiableCheckbox.data_event_id}
                />
              }
            />
          </FormField>
        </FieldSet>

        {inputs?.inputArePetsAllowed === true && (
          <FieldSet data-testid="HaveAnyPetsFieldSet">
            {/* using Text as Label does not support size more than lg */}
            <Text textStyle="heading-xs" css={{ marginBottom: '600' }} asChild>
              <p>Do you charge any pet fees?</p>
            </Text>

            <FormField
              data-testid="PetDepositFormField"
              label={<Label required={false}>Pet deposit</Label>}
              // @ts-expect-error
              error={!!errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]}
              control={
                <AdornedInput
                  size={isScreenDesktop ? 'lg' : 'md'}
                  input={
                    <AdornedInput.Input
                      name={TYPES_MAP.PET_POLICY_PET_DEPOSIT}
                      // @ts-expect-error
                      value={inputs.inputCriteriaMap[TYPES_MAP.PET_POLICY_PET_DEPOSIT].criterion}
                      placeholder="0"
                      onChange={handleInputFieldChange}
                      onBlur={handleInputFieldBlur}
                      id="pet-deposit-input"
                      aria-describedby="pet-deposit-description"
                      // @ts-expect-error
                      error={!!errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]}
                    />
                  }
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                />
              }
              description={
                // @ts-expect-error
                <FormHelp error={!!errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]}>
                  {/* @ts-expect-error */}
                  {errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]
                    ? // @ts-expect-error
                      errorObj[TYPES_MAP.PET_POLICY_PET_DEPOSIT]
                    : 'Set to $0 if you will not collect a deposit'}
                </FormHelp>
              }
            />

            {canAccessSFCostsAndFees && (
              <FormField>
                <LabeledControl
                  label={<Label>This is a one-time, non-refundable fee</Label>}
                  control={
                    <Checkbox
                      name={inputIsPetPolicyRefundable}
                      checked={inputs[inputIsPetPolicyRefundable] === false}
                      onChange={handleRefundableChange}
                    />
                  }
                />
              </FormField>
            )}

            <FormField
              data-testid="PetRentFormField"
              label={<Label required={false}>Pet rent</Label>}
              // @ts-expect-error
              error={!!errorObj[TYPES_MAP.PET_POLICY_PET_RENT]}
              control={
                <AdornedInput
                  size={isScreenDesktop ? 'lg' : 'md'}
                  input={
                    <AdornedInput.Input
                      name={TYPES_MAP.PET_POLICY_PET_RENT}
                      // @ts-expect-error
                      value={inputs.inputCriteriaMap[TYPES_MAP.PET_POLICY_PET_RENT].criterion}
                      placeholder="0"
                      onChange={handleInputFieldChange}
                      onBlur={handleInputFieldBlur}
                      id="pet-rent-input"
                      aria-describedby="pet-rent-description"
                    />
                  }
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
                />
              }
              description={
                // @ts-expect-error
                <FormHelp error={!!errorObj[TYPES_MAP.PET_POLICY_PET_RENT]}>
                  {/* @ts-expect-error */}
                  {errorObj[TYPES_MAP.PET_POLICY_PET_RENT]
                    ? // @ts-expect-error
                      errorObj[TYPES_MAP.PET_POLICY_PET_RENT]
                    : 'Set to $0 if you will not collect pet rent'}
                </FormHelp>
              }
            />
          </FieldSet>
        )}

        <Box css={{ marginTop: isInModalState ? '{spacing.-200}' : '800' }} data-testid="LearnMore">
          <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <TextButton asChild={true} textStyle="body-sm" css={{ marginRight: '200' }}>
              <a
                href="https://www.hud.gov/program_offices/fair_housing_equal_opp/assistance_animals"
                target="_blank"
                rel="noreferrer"
              >
                Learn more about assistance animal exemptions
              </a>
            </TextButton>

            <Icon css={{ color: 'icon.link.default' }} render={<IconExternalOutline />} />
          </Box>
        </Box>
        <Modal
          body={
            <div data-testid="ModalContent">
              <Text textStyle="heading-xs" css={{ marginBottom: '200' }} asChild>
                <p>Change your pet policy settings?</p>
              </Text>

              <Text asChild>
                <p>
                  Your previously added pet policy details will not be saved. &quot;Cancel&quot; to go back to editing
                  your pet policy, or &quot;Continue&quot; if you&apos;ve decided not to allow pets.
                </p>
              </Text>
            </div>
          }
          footer={
            <ButtonGroup aria-label="Pet policy actions">
              <TextButton onClick={handleCancelClick}>Cancel</TextButton>
              <Button tone="brand" emphasis="filled" onClick={handleModalContinueClick}>
                Continue
              </Button>
            </ButtonGroup>
          }
          open={displayChangeYourPetPolicyModal}
          onOpenChange={(open) => !open && handleCloseClick()}
        />
      </div>
    </Form>
  );
};

export default PetsAllowedFormField;
